<template>
  <div class="moduleStyle minHeight">
    <pageTitle title="资产快照">
      <template>
        <el-button 
          size="small" 
          type="primary" 
          :disabled="selectedRows.length !== 2"
          @click="compareSnapshots"
        >
          对比快照
        </el-button>
        <el-button 
          size="small" 
          type="warning"
          @click="showAnomaliesDialog"
        >
          查找突变点
        </el-button>
        <el-button 
          size="small" 
          type="info"
          @click="showOrderSnapshotDialog"
        >
          订单快照查询
        </el-button>
      </template>
    </pageTitle>

    <!-- 统计信息卡片 -->
    <div class="statistics-cards" style="padding: 0 20px 20px; background: #fff; margin-bottom: 10px">
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-label">当日快照数</div>
            <div class="stat-value">{{ statistics.total_count || 0 }}</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-label">异常快照数</div>
            <div class="stat-value" style="color: #f56c6c">{{ statistics.abnormal_count || 0 }}</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-label">异常率</div>
            <div class="stat-value">{{ statistics.abnormal_rate || 0 }}%</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-label">最大资产差</div>
            <div class="stat-value">{{ statistics.max_diff_yuan || 0 }} 元</div>
          </div>
        </el-col>
      </el-row>
    </div>

    <basicList 
      :config="basicListconfig" 
      @search="searchEvent"
      @handleSelectionChange="handleSelectionChange"
    >
      <template v-slot:flow_type="row">
        <span>{{ getFlowTypeName(row.row.flow_type) }}</span>
      </template>
      
      <template v-slot:link_sn="row">
        <el-link
          v-if="row.row.link_sn"
          type="primary"
          @click="queryByOrder(row.row.link_sn)"
        >
          {{ row.row.link_sn }}
        </el-link>
        <span v-else>-</span>
      </template>
      
       <template v-slot:account_balance_yuan="row">
         <span :style="{ color: row.row.account_balance_yuan < 0 ? '#f56c6c' : '' }">
           {{ row.row.account_balance_yuan }}
         </span>
       </template>
       
       <template v-slot:asset_change_yuan="row">
         <span :style="{ color: row.row.asset_change_yuan > 0 ? '#67c23a' : row.row.asset_change_yuan < 0 ? '#f56c6c' : '' }">
           {{ row.row.asset_change_yuan }}
         </span>
       </template>
       
       <template v-slot:net_profit_yuan="row">
         <span :style="{ color: row.row.net_profit_yuan > 0 ? '#67c23a' : row.row.net_profit_yuan < 0 ? '#f56c6c' : '' }">
           {{ row.row.net_profit_yuan }}
         </span>
       </template>
       
       <template v-slot:operating_profit_yuan="row">
         <span :style="{ color: row.row.operating_profit_yuan > 0 ? '#67c23a' : row.row.operating_profit_yuan < 0 ? '#f56c6c' : '' }">
           {{ row.row.operating_profit_yuan }}
         </span>
       </template>
       
       <template v-slot:asset_diff_yuan="row">
         <span :style="{ color: Math.abs(row.row.asset_diff_yuan) > 0 ? '#f56c6c' : '#67c23a' }">
           {{ row.row.asset_diff_yuan }}
         </span>
       </template>
      
      <template v-slot:is_abnormal="row">
        <el-tag 
          :type="row.row.is_abnormal === '1' ? 'danger' : 'success'" 
          size="small"
        >
          {{ row.row.is_abnormal === '1' ? '异常' : '正常' }}
        </el-tag>
      </template>
      
      <template v-slot:abnormal_level="row">
        <el-tag 
          v-if="row.row.abnormal_level" 
          :type="getAbnormalLevelType(row.row.abnormal_level)" 
          size="small"
        >
          {{ getAbnormalLevelText(row.row.abnormal_level) }}
        </el-tag>
        <span v-else>-</span>
      </template>
      
      <template v-slot:operation="row">
        <div style="display: flex; align-items: center">
          <div class="button button_modify" @click="viewDetail(row.row)">
            <i class="iconfont icon-chakan"></i> 查看
          </div>
        </div>
      </template>
    </basicList>

    <!-- 详情弹窗 -->
    <el-dialog
      title="快照详情"
      :visible.sync="detailDialogVisible"
      width="900px"
      custom-class="dialogCenter"
    >
      <el-descriptions :column="2" border v-if="detailData">
        <el-descriptions-item label="快照ID">{{ detailData.id }}</el-descriptions-item>
        <el-descriptions-item label="流水单号">{{ detailData.flow_sn }}</el-descriptions-item>
        <el-descriptions-item label="流水类型">{{ getFlowTypeName(detailData.flow_type) }}</el-descriptions-item>
        <el-descriptions-item label="关联订单号">{{ detailData.link_sn || '-' }}</el-descriptions-item>
        <el-descriptions-item label="触发事件">{{ detailData.trigger_event }}</el-descriptions-item>
        <el-descriptions-item label="快照时间">{{ detailData.snapshot_time }}</el-descriptions-item>
        <el-descriptions-item label="业务日期">{{ detailData.business_date }}</el-descriptions-item>
         <el-descriptions-item label="总资产">{{ (detailData.total_asset / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="库存资产">{{ (detailData.stock_asset / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="账户余额">{{ (detailData.account_balance / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="品牌账户余额">{{ (detailData.account_balance_brand / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="应收款">{{ (detailData.receivable / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="应付款">{{ (detailData.payable / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="客户预付款">{{ (detailData.customer_prepaid / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="供应商预付款">{{ (detailData.supplier_prepaid / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="加盟库存资产">{{ (detailData.franchise_stock_asset / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="加盟未结算">{{ (detailData.franchise_unsettled / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="营业利润">{{ (detailData.operating_profit / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="净利润">{{ (detailData.net_profit / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="昨日资产">{{ (detailData.yesterday_asset / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="资产变化">
           <span :style="{ color: detailData.asset_change > 0 ? '#67c23a' : detailData.asset_change < 0 ? '#f56c6c' : '' }">
             {{ (detailData.asset_change / 100).toFixed(2) }} 元
           </span>
         </el-descriptions-item>
         <el-descriptions-item label="资产差">
           <span :style="{ color: Math.abs(detailData.asset_diff) > 0 ? '#f56c6c' : '#67c23a' }">
             {{ (detailData.asset_diff / 100).toFixed(2) }} 元
           </span>
         </el-descriptions-item>
         <el-descriptions-item label="日常利润（正常）">{{ (detailData.daily_profit_normal / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="日常利润（香烟）">{{ (detailData.daily_profit_smoke / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="运费收入">{{ (detailData.transport_income / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="运费成本">{{ (detailData.transport_cost / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="其他收入">{{ (detailData.other_income / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="其他成本">{{ (detailData.other_cost / 100).toFixed(2) }} 元</el-descriptions-item>
         <el-descriptions-item label="加盟利润">{{ (detailData.franchise_profit / 100).toFixed(2) }} 元</el-descriptions-item>
        <el-descriptions-item label="异常状态">
          <el-tag 
            :type="detailData.is_abnormal === '1' ? 'danger' : 'success'" 
            size="small"
          >
            {{ detailData.is_abnormal === '1' ? '异常' : '正常' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="异常级别">
          <el-tag 
            v-if="detailData.abnormal_level" 
            :type="getAbnormalLevelType(detailData.abnormal_level)" 
            size="small"
          >
            {{ getAbnormalLevelText(detailData.abnormal_level) }}
          </el-tag>
          <span v-else>-</span>
        </el-descriptions-item>
        <el-descriptions-item label="备注" :span="2">{{ detailData.remark || '-' }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>

    <!-- 对比弹窗 -->
    <el-dialog
      title="快照对比"
      :visible.sync="compareDialogVisible"
      width="1000px"
      custom-class="dialogCenter"
    >
      <div v-if="compareData">
        <el-row :gutter="20">
          <el-col :span="11">
            <h4 style="text-align: center; margin-bottom: 15px">快照1（{{ compareData.snapshot1.flow_sn }}）</h4>
             <el-descriptions :column="1" border size="small">
               <el-descriptions-item label="快照时间">{{ compareData.snapshot1.snapshot_time }}</el-descriptions-item>
               <el-descriptions-item label="总资产">{{ (compareData.snapshot1.total_asset / 100).toFixed(2) }} 元</el-descriptions-item>
               <el-descriptions-item label="库存资产">{{ (compareData.snapshot1.stock_asset / 100).toFixed(2) }} 元</el-descriptions-item>
               <el-descriptions-item label="账户余额">{{ (compareData.snapshot1.account_balance / 100).toFixed(2) }} 元</el-descriptions-item>
               <el-descriptions-item label="应收款">{{ (compareData.snapshot1.receivable / 100).toFixed(2) }} 元</el-descriptions-item>
               <el-descriptions-item label="应付款">{{ (compareData.snapshot1.payable / 100).toFixed(2) }} 元</el-descriptions-item>
               <el-descriptions-item label="客户预付款">{{ (compareData.snapshot1.customer_prepaid / 100).toFixed(2) }} 元</el-descriptions-item>
               <el-descriptions-item label="供应商预付款">{{ (compareData.snapshot1.supplier_prepaid / 100).toFixed(2) }} 元</el-descriptions-item>
             </el-descriptions>
          </el-col>
          <el-col :span="2" style="display: flex; align-items: center; justify-content: center">
            <i class="el-icon-d-arrow-right" style="font-size: 32px; color: #409EFF"></i>
          </el-col>
          <el-col :span="11">
            <h4 style="text-align: center; margin-bottom: 15px">快照2（{{ compareData.snapshot2.flow_sn }}）</h4>
             <el-descriptions :column="1" border size="small">
               <el-descriptions-item label="快照时间">{{ compareData.snapshot2.snapshot_time }}</el-descriptions-item>
               <el-descriptions-item label="总资产">{{ (compareData.snapshot2.total_asset / 100).toFixed(2) }} 元</el-descriptions-item>
               <el-descriptions-item label="库存资产">{{ (compareData.snapshot2.stock_asset / 100).toFixed(2) }} 元</el-descriptions-item>
               <el-descriptions-item label="账户余额">{{ (compareData.snapshot2.account_balance / 100).toFixed(2) }} 元</el-descriptions-item>
               <el-descriptions-item label="应收款">{{ (compareData.snapshot2.receivable / 100).toFixed(2) }} 元</el-descriptions-item>
               <el-descriptions-item label="应付款">{{ (compareData.snapshot2.payable / 100).toFixed(2) }} 元</el-descriptions-item>
               <el-descriptions-item label="客户预付款">{{ (compareData.snapshot2.customer_prepaid / 100).toFixed(2) }} 元</el-descriptions-item>
               <el-descriptions-item label="供应商预付款">{{ (compareData.snapshot2.supplier_prepaid / 100).toFixed(2) }} 元</el-descriptions-item>
             </el-descriptions>
          </el-col>
        </el-row>
        <div style="margin-top: 20px">
          <h4 style="margin-bottom: 15px">差异分析</h4>
           <el-descriptions :column="2" border>
             <el-descriptions-item label="总资产差异">
               <span :style="{ color: compareData.differences_yuan.total_asset !== 0 ? '#409EFF' : '' }">
                 {{ compareData.differences_yuan.total_asset.toFixed(2) }} 元
               </span>
             </el-descriptions-item>
             <el-descriptions-item label="库存资产差异">
               <span :style="{ color: compareData.differences_yuan.stock_asset !== 0 ? '#409EFF' : '' }">
                 {{ compareData.differences_yuan.stock_asset.toFixed(2) }} 元
               </span>
             </el-descriptions-item>
             <el-descriptions-item label="账户余额差异">
               <span :style="{ color: compareData.differences_yuan.account_balance !== 0 ? '#409EFF' : '' }">
                 {{ compareData.differences_yuan.account_balance.toFixed(2) }} 元
               </span>
             </el-descriptions-item>
             <el-descriptions-item label="应收款差异">
               <span :style="{ color: compareData.differences_yuan.receivable !== 0 ? '#409EFF' : '' }">
                 {{ compareData.differences_yuan.receivable.toFixed(2) }} 元
               </span>
             </el-descriptions-item>
             <el-descriptions-item label="应付款差异">
               <span :style="{ color: compareData.differences_yuan.payable !== 0 ? '#409EFF' : '' }">
                 {{ compareData.differences_yuan.payable.toFixed(2) }} 元
               </span>
             </el-descriptions-item>
             <el-descriptions-item label="客户预付款差异">
               <span :style="{ color: compareData.differences_yuan.customer_prepaid !== 0 ? '#409EFF' : '' }">
                 {{ compareData.differences_yuan.customer_prepaid.toFixed(2) }} 元
               </span>
             </el-descriptions-item>
             <el-descriptions-item label="供应商预付款差异">
               <span :style="{ color: compareData.differences_yuan.supplier_prepaid !== 0 ? '#409EFF' : '' }">
                 {{ compareData.differences_yuan.supplier_prepaid.toFixed(2) }} 元
               </span>
             </el-descriptions-item>
           </el-descriptions>
        </div>
      </div>
    </el-dialog>

    <!-- 订单快照查询弹窗 -->
    <el-dialog
      title="订单快照查询"
      :visible.sync="orderSnapshotDialogVisible"
      width="400px"
      custom-class="dialogCenter"
    >
      <el-form label-width="100px">
        <el-form-item label="关联订单号">
          <el-input v-model="orderSnapshotQuery" placeholder="请输入订单号"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="orderSnapshotDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="queryByOrderConfirm">查询</el-button>
      </div>
    </el-dialog>

    <!-- 订单快照结果弹窗 -->
    <el-dialog
      :title="'订单快照列表（' + currentOrderSn + '）'"
      :visible.sync="orderSnapshotResultDialogVisible"
      width="1000px"
      custom-class="dialogCenter"
    >
      <el-table
        :data="orderSnapshotList"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="flow_sn" label="流水单号" width="200"></el-table-column>
        <el-table-column label="流水类型" width="120">
          <template slot-scope="scope">
            {{ getFlowTypeName(scope.row.flow_type) }}
          </template>
        </el-table-column>
         <el-table-column label="总资产" width="120" align="right">
           <template slot-scope="scope">
             {{ scope.row.total_asset_yuan }}
           </template>
         </el-table-column>
         <el-table-column label="资产变化" width="120" align="right">
           <template slot-scope="scope">
             <span :style="{ color: scope.row.asset_change_yuan > 0 ? '#67c23a' : scope.row.asset_change_yuan < 0 ? '#f56c6c' : '' }">
               {{ scope.row.asset_change_yuan }}
             </span>
           </template>
         </el-table-column>
         <el-table-column label="净利润" width="120" align="right">
           <template slot-scope="scope">
             <span :style="{ color: scope.row.net_profit_yuan > 0 ? '#67c23a' : scope.row.net_profit_yuan < 0 ? '#f56c6c' : '' }">
               {{ scope.row.net_profit_yuan }}
             </span>
           </template>
         </el-table-column>
         <el-table-column label="资产差" width="100" align="right">
           <template slot-scope="scope">
             <span :style="{ color: Math.abs(scope.row.asset_diff_yuan) > 0 ? '#f56c6c' : '#67c23a' }">
               {{ scope.row.asset_diff_yuan }}
             </span>
           </template>
         </el-table-column>
        <el-table-column prop="snapshot_time" label="快照时间" width="180"></el-table-column>
      </el-table>
    </el-dialog>

    <!-- 查找突变点弹窗 -->
    <el-dialog
      title="查找突变点"
      :visible.sync="anomaliesDialogVisible"
      width="500px"
      custom-class="dialogCenter"
    >
      <el-form label-width="120px">
        <el-form-item label="资产差阈值">
          <el-input v-model="anomaliesQuery.threshold" placeholder="请输入阈值（元），默认100">
            <template slot="append">元</template>
          </el-input>
        </el-form-item>
        <el-form-item label="日期范围">
          <el-date-picker
            v-model="anomaliesDateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          >
          </el-date-picker>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="anomaliesDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="findAnomaliesConfirm">查询</el-button>
      </div>
    </el-dialog>

    <!-- 突变点结果弹窗 -->
    <el-dialog
      title="突变点列表"
      :visible.sync="anomaliesResultDialogVisible"
      width="1100px"
      custom-class="dialogCenter"
    >
      <el-table
        :data="anomaliesList"
        border
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="flow_sn" label="流水单号" width="200"></el-table-column>
        <el-table-column label="关联订单号" width="200">
          <template slot-scope="scope">
            {{ scope.row.link_sn || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="流水类型" width="120">
          <template slot-scope="scope">
            {{ getFlowTypeName(scope.row.flow_type) }}
          </template>
        </el-table-column>
         <el-table-column label="资产变化" width="120" align="right">
           <template slot-scope="scope">
             <span :style="{ color: scope.row.asset_change_yuan > 0 ? '#67c23a' : scope.row.asset_change_yuan < 0 ? '#f56c6c' : '' }">
               {{ scope.row.asset_change_yuan }}
             </span>
           </template>
         </el-table-column>
         <el-table-column label="净利润" width="100" align="right">
           <template slot-scope="scope">
             <span :style="{ color: scope.row.net_profit_yuan > 0 ? '#67c23a' : scope.row.net_profit_yuan < 0 ? '#f56c6c' : '' }">
               {{ scope.row.net_profit_yuan }}
             </span>
           </template>
         </el-table-column>
         <el-table-column label="资产差" width="100" align="right">
           <template slot-scope="scope">
             <span style="color: #f56c6c; font-weight: bold">
               {{ scope.row.asset_diff_yuan }}
             </span>
           </template>
         </el-table-column>
        <el-table-column label="异常级别" width="100">
          <template slot-scope="scope">
            <el-tag 
              :type="getAbnormalLevelType(scope.row.abnormal_level)" 
              size="small"
            >
              {{ getAbnormalLevelText(scope.row.abnormal_level) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="snapshot_time" label="快照时间" width="180"></el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import basicList from "@/components/basicList";
import pageTitle from "@/components/pageTitle";
import { 
  getSnapshotList, 
  getSnapshotOne, 
  compareSnapshots as apiCompareSnapshots,
  getSnapshotByOrder,
  getAbnormalStats,
  findAnomalies as apiFindAnomalies
} from "@/api/assetSnapshot";

const defaultParameters = {
  page: 1,
  limit: 10,
};

export default {
  name: "AssetSnapshot",
  components: {
    basicList,
    pageTitle,
  },
  data() {
    return {
      // 统计信息
      statistics: {
        total_count: 0,
        abnormal_count: 0,
        abnormal_rate: 0,
        max_diff_yuan: 0,
      },
      
      // 选中记录
      selectedRows: [],
      
      // 列表组件参数
      basicListconfig: {
        loading: false,
        height: "calc(100vh - 450px)",
        check: true,
        search: [
          {
            label: "流水单号",
            type: "input",
            key: "flow_sn",
          },
          {
            label: "关联订单号",
            type: "input",
            key: "link_sn",
          },
          {
            label: "流水类型",
            type: "select",
            options: [],
            key: "flow_type",
          },
          {
            label: "异常状态",
            type: "select",
            options: [
              { label: "全部", value: "" },
              { label: "正常", value: "0" },
              { label: "异常", value: "1" },
            ],
            key: "is_abnormal",
          },
          {
            label: "异常级别",
            type: "select",
            options: [
              { label: "全部", value: "" },
              { label: "轻微", value: "low" },
              { label: "中度", value: "medium" },
              { label: "严重", value: "high" },
              { label: "紧急", value: "critical" },
            ],
            key: "abnormal_level",
          },
          {
            label: "日期",
            type: "daterange",
            key: "date_range",
          },
        ],
        data: [],
        column: [
          {
            label: "ID",
            prop: "id",
            width: 80,
          },
          {
            label: "流水类型",
            prop: "flow_type",
            slot: true,
            width: 120,
          },
          {
            label: "关联订单号",
            prop: "link_sn",
            slot: true,
            width: 200,
          },
          {
            label: "资产差",
            prop: "asset_diff_yuan",
            slot: true,
            width: 120,
            align: "right",
          },
          {
            label: "异常状态",
            prop: "is_abnormal",
            slot: true,
            width: 100,
          },
          {
            label: "异常级别",
            prop: "abnormal_level",
            slot: true,
            width: 100,
          },
          {
            label: "净利润",
            prop: "net_profit_yuan",
            slot: true,
            width: 120,
            align: "right",
          },
          {
            label: "营业利润",
            prop: "operating_profit_yuan",
            slot: true,
            width: 120,
            align: "right",
          },
           {
             label: "总资产",
             prop: "total_asset_yuan",
             width: 140,
             align: "right",
           },
           {
             label: "库存资产",
             prop: "stock_asset_yuan",
             width: 140,
             align: "right",
           },
           {
             label: "账户余额",
             prop: "account_balance_yuan",
             slot: true,
             width: 140,
             align: "right",
           },
           {
             label: "应收款",
             prop: "receivable_yuan",
             width: 120,
             align: "right",
           },
           {
             label: "应付款",
             prop: "payable_yuan",
             width: 120,
             align: "right",
           },
          {
            label: "资产变化",
            prop: "asset_change_yuan",
            slot: true,
            width: 120,
            align: "right",
          },
          {
            label: "快照时间",
            prop: "snapshot_time",
            width: 180,
          },
          {
            label: "业务日期",
            prop: "business_date",
            width: 120,
          },
        ],
        isPage: true,
        operation: true,
        operationWidth: 120,
        total: 0,
        searchParameters: Object.assign({}, defaultParameters),
      },
      
      // 详情弹窗
      detailDialogVisible: false,
      detailData: null,
      
      // 对比弹窗
      compareDialogVisible: false,
      compareData: null,
      
      // 订单快照查询
      orderSnapshotDialogVisible: false,
      orderSnapshotQuery: "",
      orderSnapshotResultDialogVisible: false,
      orderSnapshotList: [],
      currentOrderSn: "",
      
      // 突变点查询
      anomaliesDialogVisible: false,
      anomaliesQuery: {
        threshold: "100",
      },
      anomaliesDateRange: [],
      anomaliesResultDialogVisible: false,
      anomaliesList: [],
      
      // 流水类型映射
      flowTypeMap: {
        0: "预采单",
        1: "销售单",
        "-1": "采购单",
        2: "转账入",
        "-2": "转账出",
        3: "合并收款",
        "-3": "合并付款",
        4: "冲帐收入",
        "-4": "冲帐支出",
        5: "额外收入",
        "-5": "额外支出",
        6: "加盟入",
        "-6": "加盟出",
        7: "预付收入",
        "-7": "预付支出",
        8: "客户余额充值",
        "-8": "客户余额抵扣",
        9: "供应商余额充值",
        "-9": "供应商余额抵扣",
        10: "撤销收入",
        "-10": "撤销支出",
        11: "运费收入",
        "-11": "运费支出",
        "-12": "外卖运费",
        13: "刷单收入",
        "-13": "刷单支出",
        "-14": "外卖额外支出",
      },
    };
  },
  created() {
    this.initFlowTypeOptions();
    this.loadAbnormalStats();
    this.getlist();
  },
  methods: {
    // 初始化流水类型选项
    initFlowTypeOptions() {
      const flowTypeOptions = Object.keys(this.flowTypeMap).map(key => ({
        label: this.flowTypeMap[key],
        value: parseInt(key),
      }));
      this.basicListconfig.search.find(item => item.key === "flow_type").options = [
        { label: "全部", value: "" },
        ...flowTypeOptions
      ];
    },
    
    // 获取列表数据
    getlist() {
      this.basicListconfig.loading = true;
      const params = { ...this.basicListconfig.searchParameters };
      
      // 处理日期范围
      if (params.date_range && params.date_range.length === 2) {
        params.start_date = params.date_range[0];
        params.end_date = params.date_range[1];
        delete params.date_range;
      }
      
      getSnapshotList(params)
        .then((res) => {
          if (res.code === 0 || res.code === 1) {
            this.basicListconfig.data = res.data || [];
            this.basicListconfig.total = res.count || 0;
          } else {
            this.$message.error(res.msg || "获取数据失败");
          }
        })
        .catch((err) => {
          this.$message.error("获取数据失败");
          console.error(err);
        })
        .finally(() => {
          this.basicListconfig.loading = false;
        });
    },
    
    // 加载异常统计
    loadAbnormalStats() {
      const today = new Date();
      const params = {
        start_date: today.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0],
      };
      
      getAbnormalStats(params)
        .then((res) => {
          if (res.code === 1 && res.data && res.data.length > 0) {
            const todayStats = res.data[0];
            this.statistics = {
              total_count: todayStats.total_count || 0,
              abnormal_count: todayStats.abnormal_count || 0,
              abnormal_rate: todayStats.abnormal_rate ? todayStats.abnormal_rate.toFixed(2) : 0,
              max_diff_yuan: todayStats.max_diff_yuan || 0,
            };
          }
        })
        .catch((err) => {
          console.error("获取统计信息失败", err);
        });
    },
    
    // 搜索事件
    searchEvent(data, page, pageSize) {
      if (page || pageSize) {
        if (page) this.basicListconfig.searchParameters.page = page;
        if (pageSize) this.basicListconfig.searchParameters.limit = pageSize;
        this.getlist();
      } else if (data) {
        for (const key in data) {
          this.basicListconfig.searchParameters[key] = data[key];
        }
        this.basicListconfig.searchParameters.page = 1;
        this.getlist();
      }
    },
    
    // 选择变化
    handleSelectionChange(rows) {
      this.selectedRows = rows;
    },
    
    // 查看详情
    viewDetail(row) {
      getSnapshotOne({ id: row.id })
        .then((res) => {
          if (res.code === 1) {
            this.detailData = res.data;
            this.detailDialogVisible = true;
          } else {
            this.$message.error(res.msg || "获取详情失败");
          }
        })
        .catch((err) => {
          this.$message.error("获取详情失败");
          console.error(err);
        });
    },
    
    // 对比快照
    compareSnapshots() {
      if (this.selectedRows.length !== 2) {
        this.$message.warning("请选择两个快照进行对比");
        return;
      }
      
      const params = {
        id1: this.selectedRows[0].id,
        id2: this.selectedRows[1].id,
      };
      
      apiCompareSnapshots(params)
        .then((res) => {
          if (res.code === 1) {
            this.compareData = res.data;
            this.compareDialogVisible = true;
          } else {
            this.$message.error(res.msg || "对比失败");
          }
        })
        .catch((err) => {
          this.$message.error("对比失败");
          console.error(err);
        });
    },
    
    // 显示订单快照查询弹窗
    showOrderSnapshotDialog() {
      this.orderSnapshotQuery = "";
      this.orderSnapshotDialogVisible = true;
    },
    
    // 根据订单号查询
    queryByOrder(orderSn) {
      getSnapshotByOrder({ link_sn: orderSn })
        .then((res) => {
          if (res.code === 1) {
            this.orderSnapshotList = res.data || [];
            this.currentOrderSn = orderSn;
            this.orderSnapshotResultDialogVisible = true;
          } else {
            this.$message.error(res.msg || "查询失败");
          }
        })
        .catch((err) => {
          this.$message.error("查询失败");
          console.error(err);
        });
    },
    
    // 确认订单查询
    queryByOrderConfirm() {
      if (!this.orderSnapshotQuery) {
        this.$message.warning("请输入订单号");
        return;
      }
      this.orderSnapshotDialogVisible = false;
      this.queryByOrder(this.orderSnapshotQuery);
    },
    
    // 显示突变点查询弹窗
    showAnomaliesDialog() {
      this.anomaliesQuery.threshold = "100";
      this.anomaliesDateRange = [];
      this.anomaliesDialogVisible = true;
    },
    
    // 查找突变点确认
    findAnomaliesConfirm() {
      const params = {
        threshold: (parseFloat(this.anomaliesQuery.threshold) || 100) * 100, // 转换为分
      };
      
      if (this.anomaliesDateRange && this.anomaliesDateRange.length === 2) {
        params.start_date = this.anomaliesDateRange[0];
        params.end_date = this.anomaliesDateRange[1];
      }
      
      apiFindAnomalies(params)
        .then((res) => {
          if (res.code === 1) {
            this.anomaliesList = res.data || [];
            this.anomaliesDialogVisible = false;
            this.anomaliesResultDialogVisible = true;
          } else {
            this.$message.error(res.msg || "查询失败");
          }
        })
        .catch((err) => {
          this.$message.error("查询失败");
          console.error(err);
        });
    },
    
    // 获取流水类型名称
    getFlowTypeName(type) {
      return this.flowTypeMap[type] || type;
    },
    
    // 获取异常级别文本
    getAbnormalLevelText(level) {
      const levelMap = {
        low: "轻微",
        medium: "中度",
        high: "严重",
        critical: "紧急",
      };
      return levelMap[level] || level;
    },
    
    // 获取异常级别类型（用于tag颜色）
    getAbnormalLevelType(level) {
      const typeMap = {
        low: "info",
        medium: "warning",
        high: "danger",
        critical: "danger",
      };
      return typeMap[level] || "info";
    },
  },
};
</script>

<style scoped>
.stat-card {
  padding: 20px;
  background: #f5f7fa;
  border-radius: 4px;
  text-align: center;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
}

.button {
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 3px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s;
}

.button_modify {
  color: #409eff;
  border: 1px solid #409eff;
}

.button_modify:hover {
  background: #409eff;
  color: #fff;
}
</style>




// WEBPACK FOOTER //
// src/views/assetSnapshot/index.vue